import React from "react";

import cx from "classnames";

import { Button } from "../Button";
import { Flex } from "../Flex";
import { Text } from "../Text";

import styles from "./EntitySearchBar.scss";
import SearchWithTypeAhead, { defaultI18n } from "./SearchWithTypeAhead";

const ITEM_ROW_SIZE = 45; // pixels
function highlight(original, query) {
  const parts = original.split(new RegExp(`(${query})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <b key={i}>{part}</b>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

/**
 * It scrolls up the menu list with the new highlighted item
 */
export function scrollUp(menu, newIndex, visibleItems) {
  const currentPosition = menu.scrollTop;
  const lastItemIndex = visibleItems.length - 1;
  let newPosition;

  if (newIndex === lastItemIndex) {
    // Go to the end
    // Only 3 items fits in each list view
    newPosition = (visibleItems.length - 3) * ITEM_ROW_SIZE;
  } else {
    newPosition = currentPosition - ITEM_ROW_SIZE;
  }
  menu.scrollTo(0, newPosition <= 0 ? 0 : newPosition);
}

/**
 * It scrolls down the menu list with the new highlighted item
 */
export function scrollDown(menu, newIndex) {
  let currentPosition = menu.scrollTop;
  let newPosition = 0;

  // Start scrolling only when the new index is beyond the third element
  if (newIndex > 1) {
    newPosition = currentPosition + ITEM_ROW_SIZE;
  }
  menu.scrollTo(0, newPosition);
}

export function closeMenuOnSubmit(setMenuOpen, onSubmit) {
  return (value) => {
    setMenuOpen(false);
    onSubmit(value);
  };
}

/**
 * @typedef {object} FilteredItem
 * @prop {string} id
 * @prop {string} text
 */

/**
 * @typedef {object} EntityResultProps
 * @param {FilteredItem} item
 * @param {string} query The search query
 * @param {number} index The index of this item in the list
 * @param {number} selectedIndex The index of the item selected in the list
 * @param {Function} setSelectedIndex For hovering
 * @param {Function} onSubmit Called when clicking over any selected item
 */

/**
 * An uncontrolled user search box with type-ahead completion.
 * @param {UserResultProps} props
 */
export function EntityResult({
  item,
  query,
  index,
  selectedIndex,
  setSelectedIndex,
  onSubmit,
}) {
  return (
    <button
      className={cx(styles.entityRow, {
        [styles.entityRowSelected]: index === selectedIndex,
      })}
      onMouseOver={() => setSelectedIndex(index)}
      onFocus={() => setSelectedIndex(index)}
      onClick={() => onSubmit(item)}
    >
      <Flex direction="column" className={styles.name}>
        <Text size="small" className={styles.trim}>
          {highlight(item.text, query)}
        </Text>
      </Flex>
    </button>
  );
}

/**
 * @typedef {object} SearchI18n
 * @prop {string} [search="Search for"]
 * @prop {string} [clear="Clear"]
 */

/**
 * @typedef {object} EntitySearchBarProps
 * @prop {string} [id="entity-search-bar-id"] The ID of the input box
 * @prop {string} [defaultValue=""] The default value of the search box
 * @prop {FilteredItem[]} [filteredItems=[]] Items that already match its name with the value we search for
 * @prop {Function} onChange When the value of the search changes, we invoke this function. Signature: `(value: string) => void`
 * @prop {Function} [onClear] If set, we add a "clear" button to the left of the search icon. When that button is activated, we call this function
 * @prop {Function} onSubmit When a user wants to search, we invoke this function. Signature: `(selectedItem: string | FilteredItem) => void`
 * @prop {any} [inputProps={}] Props to attach to the input box
 * @prop {boolean} [fullWidth=false]
 * @prop {SearchI18n} [i18n]
 * @prop {boolean} [allowMenuOpenInFirstRender] Allows the Menu to be displayed in the first render, useful when having a defaultValue != "" set
 */

/**
 * A semi-controlled search box with type-ahead completion. It requires of the items to display being already filtered
 * @param {EntitySearchBarProps} props
 */
// eslint-disable-next-line max-lines-per-function
export function EntitySearchBar({
  id = "entity-search-bar-id",
  allowMenuOpenInFirstRender = false,
  defaultValue = "",
  filteredItems = [],
  fullWidth = false,
  onChange,
  onClear,
  onSubmit,
  inputProps,
  i18n = {},
}) {
  const i18nValues = Object.assign({}, defaultI18n, i18n);
  const [value, setValue] = React.useState(defaultValue);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [menuId] = React.useState("list-items-menu");
  const [isFirstRender, setIsFirstRender] = React.useState(
    !allowMenuOpenInFirstRender
  );
  const [menuOpen, setMenuOpen] = React.useState(
    value.length >= 2 && !isFirstRender
  );
  const [typeAheadResults, visibleItems] = React.useMemo(() => {
    // Show nothing if the value length is below our minimum size
    if (value.length < 2 || isFirstRender) {
      return [[], []];
    }
    const resultsElements = [
      value.length > 0 ? (
        <Button
          variant="transparent"
          onClick={() => closeMenuOnSubmit(setMenuOpen, onSubmit)(value)}
          className={styles.searchFor}
          key="default"
        >
          <Text size="small" paragraph color="#AAA">
            {i18nValues.search}: <b className={styles.query}>{value}</b>
          </Text>
        </Button>
      ) : null,
      filteredItems.map((item, i) => {
        return (
          <EntityResult
            item={item}
            key={item.id}
            query={value}
            selectedIndex={selectedIndex}
            index={i}
            setSelectedIndex={setSelectedIndex}
            onSubmit={closeMenuOnSubmit(setMenuOpen, onSubmit)}
          />
        );
      }),
    ];
    return [resultsElements, filteredItems];
  }, [value, filteredItems, selectedIndex]);

  React.useEffect(() => {
    if (!menuOpen) {
      setMenuOpen(true);
    }
  }, [value]);

  /* Prevents the Menu to be open in the first render, when having a value set */
  React.useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, []);

  return (
    <SearchWithTypeAhead
      id={id}
      menuId={menuId}
      value={value}
      fullWidth={fullWidth}
      onChange={(newValue) => {
        setValue(newValue);
        onChange(newValue);
      }}
      onClear={() => {
        setValue("");
        onClear ? onClear() : null;
      }}
      onSubmit={closeMenuOnSubmit(setMenuOpen, onSubmit)}
      typeAheadResults={typeAheadResults}
      menuOpen={menuOpen}
      onSetOpen={setMenuOpen}
      i18n={i18n}
      inputProps={{
        ...inputProps,
        onKeyDown: (e) => {
          if (e.key === "Escape") {
            setMenuOpen(false);
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (
              visibleItems.length === 0 ||
              selectedIndex < 0 ||
              selectedIndex >= visibleItems.length
            ) {
              onSubmit(value);
            } else {
              onSubmit(visibleItems[selectedIndex]);
            }
            setMenuOpen(false);
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            // get the new highlighted item index
            const newIndex =
              selectedIndex > 0 ? selectedIndex - 1 : visibleItems.length - 1;

            scrollUp(document.getElementById(menuId), newIndex, visibleItems);
            setSelectedIndex(newIndex);
            setMenuOpen(true);
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            // get the new highlighted item index
            const newIndex =
              selectedIndex < visibleItems.length - 1 ? selectedIndex + 1 : 0;

            scrollDown(document.getElementById(menuId), newIndex);
            setSelectedIndex(newIndex);
            setMenuOpen(true);
          }
        },
      }}
    />
  );
}
